import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import Modal from "react-responsive-modal";
import { ArrowLeft, ArrowRight, Repeat2 } from "lucide-react";
import { SpinnerCircularFixed } from "spinners-react";
import {
  ChangeProductBody,
  ProductChangeCandidate,
} from "../../interfaces/sales";
import { ErrorModalContext } from "../AlertsModal/AlertsModal";
import { getServiceError } from "../../utils/getServiceError";
import { ProductChangeSelectionStep } from "./ProductSelectionStep";
import { ReplacementSelectionStep } from "./ReplacementSelectionStep";

type ProductChangeStep = "products" | "replacements" | "confirm";

const STEP_TITLES: Record<ProductChangeStep, string> = {
  products: "Seleccionar productos",
  replacements: "Elegir producto de reemplazo",
  confirm: "Confirmar cambio de producto",
};

export interface ProductChangeModalProps {
  open: boolean;
  closeModal: () => void;
  /** Cómo se nombra el registro que se está corrigiendo: "venta" o "cuenta" */
  entityLabel: string;
  /** Código de la venta o nombre de la cuenta, mostrado en la confirmación */
  entityCode: string;
  loadOptions: () => Promise<ProductChangeCandidate[]>;
  submitChanges: (body: ChangeProductBody) => Promise<unknown>;
  onSuccess: () => void;
}

/**
 * YOSHI-10261: modal de "Cambiar producto", compartido por Ventas y Cuentas. Solo ofrece reemplazos
 * que el backend ya validó (misma concesión y mismo precio exacto), por lo que el cobro nunca cambia:
 * únicamente cambia qué producto quedó registrado y el inventario se corrige con movimientos nuevos
 * de tipo "Cambio de producto".
 */
export const ProductChangeModal: React.FC<ProductChangeModalProps> = ({
  open,
  closeModal,
  entityLabel,
  entityCode,
  loadOptions,
  submitChanges,
  onSuccess,
}) => {
  const { showAlert } = useContext(ErrorModalContext);
  const [step, setStep] = useState<ProductChangeStep>("products");
  const [candidates, setCandidates] = useState<ProductChangeCandidate[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [replacements, setReplacements] = useState<Record<string, string>>({});
  const [isLoadingOptions, setIsLoadingOptions] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const reset = useCallback(() => {
    setStep("products");
    setSelectedProducts([]);
    setReplacements({});
    setIsLoading(false);
  }, []);

  // las opciones se piden cada vez que se abre el modal, para que reflejen el estado actual
  useEffect(() => {
    if (!open) {
      reset();
      return;
    }

    let isActive = true;
    setIsLoadingOptions(true);
    loadOptions()
      .then((options: ProductChangeCandidate[]) => {
        if (isActive) {
          setCandidates(options);
        }
      })
      .catch((error) => {
        if (isActive) {
          setCandidates([]);
          showAlert(getServiceError(error), "error");
        }
      })
      .finally(() => {
        if (isActive) {
          setIsLoadingOptions(false);
        }
      });

    return () => {
      isActive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const selectedCandidates: ProductChangeCandidate[] = useMemo(
    () =>
      candidates.filter((candidate) =>
        selectedProducts.includes(candidate.uuid_sale_product),
      ),
    [candidates, selectedProducts],
  );

  const hasSelectedProducts: boolean = selectedProducts.length > 0;

  const areReplacementsComplete: boolean = useMemo(
    () =>
      hasSelectedProducts &&
      selectedProducts.every(
        (uuidSaleProduct: string) => !!replacements[uuidSaleProduct],
      ),
    [hasSelectedProducts, selectedProducts, replacements],
  );

  const handleToggleProduct = useCallback((uuidSaleProduct: string) => {
    setSelectedProducts((previous: string[]) =>
      previous.includes(uuidSaleProduct)
        ? previous.filter((uuid: string) => uuid !== uuidSaleProduct)
        : [...previous, uuidSaleProduct],
    );
    // al deseleccionar, el reemplazo elegido deja de aplicar
    setReplacements((previous: Record<string, string>) => {
      const next: Record<string, string> = { ...previous };
      delete next[uuidSaleProduct];
      return next;
    });
  }, []);

  const handleReplacementChange = useCallback(
    (uuidSaleProduct: string, uuidProduct: string) => {
      setReplacements((previous: Record<string, string>) => {
        const next: Record<string, string> = { ...previous };
        if (uuidProduct) {
          next[uuidSaleProduct] = uuidProduct;
        } else {
          delete next[uuidSaleProduct];
        }
        return next;
      });
    },
    [],
  );

  const goToNextStep = useCallback(() => {
    if (step === "products" && hasSelectedProducts) {
      setStep("replacements");
      return;
    }
    if (step === "replacements" && areReplacementsComplete) {
      setStep("confirm");
    }
  }, [step, hasSelectedProducts, areReplacementsComplete]);

  const goToPreviousStep = useCallback(() => {
    if (step === "confirm") {
      setStep("replacements");
      return;
    }
    if (step === "replacements") {
      setStep("products");
    }
  }, [step]);

  const submit = useCallback(async () => {
    try {
      setIsLoading(true);
      await submitChanges({
        products: selectedProducts.map((uuidSaleProduct: string) => ({
          uuid_sale_product: uuidSaleProduct,
          uuid_product: replacements[uuidSaleProduct],
        })),
      });
      setIsLoading(false);
      closeModal();
      showAlert(
        `Los productos de la ${entityLabel} ${entityCode} se cambiaron correctamente.`,
        "success",
        "Cambio de producto exitoso",
      );
      onSuccess();
    } catch (error) {
      setIsLoading(false);
      showAlert(getServiceError(error), "error");
    }
  }, [
    submitChanges,
    selectedProducts,
    replacements,
    closeModal,
    showAlert,
    entityLabel,
    entityCode,
    onSuccess,
  ]);

  const getReplacementName = (candidate: ProductChangeCandidate): string =>
    candidate.options.find(
      (option) => option.uuid === replacements[candidate.uuid_sale_product],
    )?.name ?? "";

  return (
    <Modal
      classNames={{
        modal: "bg-white w-auto sm:w-[550px] rounded-2xl shadow-modal !p-0",
        overlay: "bg-modal-overlay",
      }}
      showCloseIcon={false}
      open={open}
      onClose={closeModal}
      center
      focusTrapped={false}
    >
      <div className="p-8 sm:p-8 sm:pb-6">
        <h1 className="border-b border-gray-200 pb-4 text-xl font-bold text-gray-900">
          {STEP_TITLES[step]}
        </h1>

        <div className="mb-6 mt-6">
          {isLoadingOptions ? (
            <div className="flex justify-center py-10">
              <SpinnerCircularFixed
                size={32}
                color="#000000"
                secondaryColor="#E5E7EB"
              />
            </div>
          ) : candidates.length === 0 ? (
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-center">
              <p className="text-sm text-gray-600">
                No hay productos que se puedan cambiar
              </p>
              <p className="text-xs text-gray-400">
                {`Los productos de esta ${entityLabel} no tienen otro producto de la misma concesión y del mismo precio.`}
              </p>
            </div>
          ) : (
            <>
              {step === "products" && (
                <ProductChangeSelectionStep
                  candidates={candidates}
                  selectedProducts={selectedProducts}
                  onToggleProduct={handleToggleProduct}
                />
              )}

              {step === "replacements" && (
                <ReplacementSelectionStep
                  candidates={selectedCandidates}
                  replacements={replacements}
                  onReplacementChange={handleReplacementChange}
                />
              )}

              {step === "confirm" && (
                <div className="space-y-4">
                  <p className="text-gray-600">
                    Se cambiarán los productos seleccionados de la {entityLabel}{" "}
                    <span className="font-bold text-black">{entityCode}</span>.
                    El total cobrado no cambia y se generarán los movimientos de
                    inventario de tipo{" "}
                    <span className="font-bold text-black">
                      Cambio de producto
                    </span>{" "}
                    que apliquen.
                  </p>
                  <div className="max-h-40 space-y-1 overflow-y-auto rounded-lg bg-gray-50 p-3">
                    {selectedCandidates.map((candidate) => (
                      <div
                        key={candidate.uuid_sale_product}
                        className="flex items-center justify-between gap-2 text-sm"
                      >
                        <span className="text-gray-700">
                          {candidate.product} × {candidate.quantity}
                        </span>
                        <span className="flex items-center gap-2 font-medium text-indigo-600">
                          <ArrowRight className="h-3.5 w-3.5 shrink-0" />
                          {getReplacementName(candidate)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex flex-col justify-end gap-3 sm:flex-row">
          {step === "products" ? (
            <button
              className="h-12 rounded-lg border border-gray-200 bg-white px-6 py-2 text-sm text-gray-900 transition-colors duration-300 hover:bg-gray-100"
              onClick={closeModal}
            >
              Descartar
            </button>
          ) : (
            <button
              className="flex h-12 items-center gap-2 rounded-lg border border-gray-200 bg-white px-6 py-2 text-sm text-gray-900 transition-colors duration-300 hover:bg-gray-100"
              onClick={goToPreviousStep}
            >
              <ArrowLeft className="h-4 w-4" />
              Atrás
            </button>
          )}

          {step === "confirm" ? (
            <button
              className="flex h-12 items-center justify-center gap-2 rounded-lg bg-indigo-600 px-6 py-2 text-sm text-white transition-colors duration-300 hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
              disabled={isLoading}
              onClick={submit}
            >
              {isLoading ? (
                <SpinnerCircularFixed
                  size={16}
                  color="#FFFFFF"
                  secondaryColor="#000000"
                />
              ) : (
                <Repeat2 className="h-4 w-4 shrink-0" />
              )}
              Confirmar cambio
            </button>
          ) : (
            <button
              className="flex h-12 items-center justify-center gap-2 rounded-lg bg-gray-900 px-6 py-2 text-sm text-white transition-colors duration-300 hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
              disabled={
                isLoadingOptions ||
                candidates.length === 0 ||
                (step === "products" && !hasSelectedProducts) ||
                (step === "replacements" && !areReplacementsComplete)
              }
              onClick={goToNextStep}
            >
              Siguiente
              <ArrowRight className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
};
